'use strict';

import React from 'react';
import * as ReactDOMClient from 'react-dom/client';
import ReCAPTCHA from 'react-google-recaptcha';
import ResearchAreas from './ResearchAreas.jsx';
import axios from 'axios';
import cn from 'classnames';

const e = React.createElement;

export const ResearchAreaSelection = React.createContext();

const domContainer = document.querySelector('#rf-root');
const root = ReactDOMClient.createRoot(domContainer);
const tokenElt = domContainer.querySelectorAll('[name="__RequestVerificationToken"]');
let token = '';
if (tokenElt && tokenElt.length) {
    token = tokenElt[0].getAttribute('value');
}
const postPath = domContainer.getAttribute('data-post');
const getResAreasPath = domContainer.getAttribute('data-get-resareas');
const recaptchaKey = domContainer.getAttribute('data-recaptcha-key');

class Form extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            submitted: false,
            submitting: 0,
            page: 1,
            firstName: '',
            lastName: '',
            email: '',
            confirmEmail: '',
            upload: null,
            uploadName: '',
            website: '',
            selectedAreas: [],
            otherAreas: '',
            recaptcha: '',
            uploadValidation: '',
            invalid: [],
            retries: 0,
            failed: false
        };
        this.handleApply = this.handleApply.bind(this);
        this.handleRefresh = this.handleRefresh.bind(this);
        this.handleScrollToTop = this.handleScrollToTop.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.handleRecaptchaChange = this.handleRecaptchaChange.bind(this);
        this.handleValidations = this.handleValidations.bind(this);
        this.handleUpload = this.handleUpload.bind(this);
        this.handleDeleteUpload = this.handleDeleteUpload.bind(this);
        this.handleDeleteUploadKeyDown = this.handleDeleteUploadKeyDown.bind(this);
        this.handleDeleteSelection = this.handleDeleteSelection.bind(this);
        this.handleDeleteSelectionKeyDown = this.handleDeleteSelectionKeyDown.bind(this);
        this.handleClearSelections = this.handleClearSelections.bind(this);
        this.handleNext = this.handleNext.bind(this);
        this.handleBack = this.handleBack.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleClickWhileSubmitting = this.handleClickWhileSubmitting.bind(this);
        this.recaptchaRef = React.createRef();
        this.uploadRef = React.createRef();
    }

    static arrayAdd(arr, val) {
        if (Array.isArray(arr) && arr.indexOf(val) < 0) {
            arr.push(val);
        }
    }

    static arrayRemove(arr, val) {
        if (Array.isArray(arr)) {
            while (arr.indexOf(val) >= 0) {
                arr.splice(arr.indexOf(val), 1);
            }
        }
    }

    handleApply(e) {
        e.preventDefault();
        this.setState({
            page: 2,
            submitted: false,
            failed: false
        });

        let elts = document.getElementsByClassName('rf-content');
        while (elts[0]) {
            elts[0].parentNode.removeChild(elts[0]);
        }

        for (const div of document.querySelectorAll('.rf-omb')) {
            div.classList.add('show');
        }
    }

    handleRefresh(e) {
        e.preventDefault();
        this.handleScrollToTop();
        window.location.reload();
    }

    handleScrollToTop() {
        // Add slight delay, since this sometimes fails if rendering is incomplete
        setTimeout(function () {
            const pageTitle = document.querySelector('main');
            if (pageTitle) {
                try {
                    pageTitle.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                        inline: 'nearest'
                    });
                } catch (_) {
                    pageTitle.scrollIntoView(true);
                }
            }
        }, 250);
    }

    handleChange(e) {
        this.setState({
            [e.target.name]: e.target.value
        }, this.handleValidations);
    }

    handleRecaptchaChange(value) {
        let invalid = [...this.state.invalid];
        Form[value ? 'arrayRemove' : 'arrayAdd'](invalid, 'recaptcha');
        this.setState({
            recaptcha: value,
            invalid: invalid
        });
    }

    handleUpload(e) {
        let file = (e && e.target && e.target.files && e.target.files.length) ? e.target.files[0] : null;
        if (file) {
            let reader = new FileReader();
            let invalid = [...this.state.invalid];
            Form.arrayAdd(invalid, 'upload');
            reader.readAsDataURL(file);
            reader.onload = () => {
                if (!file.size) {
                    this.setState({
                        upload: null,
                        uploadName: '',
                        uploadValidation: 'Your file is empty (0 bytes). Please upload a file with content.',
                        invalid: invalid
                    });
                }
                else if (file.size > (4 * 1024 * 1024)) {
                    this.setState({
                        upload: null,
                        uploadName: '',
                        uploadValidation: 'Your file exceeds the maximum size of 4 MB.',
                        invalid: invalid
                    });
                }
                else if (
                    file.type !== 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' &&
                    file.type !== 'application/msword' &&
                    file.type !== 'application/pdf'
                ) {
                    this.setState({
                        upload: null,
                        uploadName: '',
                        uploadValidation: 'Please upload the appropriate file type (.doc, .docx, or .pdf).',
                        invalid: invalid
                    });
                }
                else {
                    Form.arrayRemove(invalid, 'upload');
                    this.setState({
                        upload: file,
                        uploadName: file.name,
                        uploadValidation: '',
                        invalid: invalid
                    });
                }
            };
        }
    }

    handleDeleteUpload() {
        this.uploadRef.current.value = null;
        this.setState({
            upload: null,
            uploadName: ''
        });
    }

    handleDeleteUploadKeyDown(e) {
        if (e.which === 13) {
            this.handleDeleteUpload(e);
        }
    }

    handleDeleteSelection(e) {
        this.setState({
            selectedAreas: this.state.selectedAreas.filter(area => area !== e.target.getAttribute('data-val'))
        });
    }

    handleDeleteSelectionKeyDown(e) {
        if (e.which === 13) {
            this.handleDeleteSelection(e);
        }
    }

    handleClearSelections(e) {
        e.preventDefault();
        this.setState({
            selectedAreas: []
        });
    }

    handleValidations() {
        let state = this.state;
        let invalid = [...state.invalid];
        if (state.firstName) Form.arrayRemove(invalid, 'firstName');
        if (state.lastName) Form.arrayRemove(invalid, 'lastName');
        if (state.email) Form.arrayRemove(invalid, 'email');
        if (state.confirmEmail) Form.arrayRemove(invalid, 'confirmEmail');
        if (state.otherAreas) Form.arrayRemove(invalid, 'otherAreas');
        this.setState({
            invalid: invalid
        });
    }

    handleNext() {
        this.handleValidations();
        const trim = s => s.replace(/^\s+|\s+$/g, '');
        let state = this.state;
        let invalid = [...state.invalid];
        let uploadValidation = '';

        if (!trim(state.firstName)) Form.arrayAdd(invalid, 'firstName');
        if (!trim(state.lastName)) Form.arrayAdd(invalid, 'lastName');
        let email = trim(state.email);
        let confirmEmail = trim(state.confirmEmail);
        let isEmailValid = email && email.match(/.+@.+\..+/);
        if (!isEmailValid) Form.arrayAdd(invalid, 'email');
        if (!confirmEmail || confirmEmail !== email) Form.arrayAdd(invalid, 'confirmEmail');
        if (!state.upload) {
            Form.arrayAdd(invalid, 'upload');
            uploadValidation = 'Please select your resume file for upload.';
        } else {
            Form.arrayRemove(invalid, 'upload');
        }

        if (invalid.length) {
            let obj = { invalid: invalid };
            if (uploadValidation) {
                obj.uploadName = '';
                obj.uploadValidation = uploadValidation;
            }
            this.setState(obj);
        } else {
            this.setState({
                page: state.page + 1,
                firstName: trim(state.firstName),
                lastName: trim(state.lastName),
                email: trim(state.email),
                confirmEmail: trim(state.confirmEmail),
                invalid: invalid
            });

            for (const div of document.querySelectorAll('.rf-omb')) {
                div.classList.remove('show');
            }

            if (document.activeElement != document.body) document.activeElement.blur();
            this.handleScrollToTop();
        }
    }

    handleBack(e) {
        e.preventDefault();
        this.setState({
            page: this.state.page - 1,
            invalid: []
        });

        for (const div of document.querySelectorAll('.rf-omb')) {
            div.classList.add('show');
        }

        if (document.activeElement !== document.body) document.activeElement.blur();
        this.handleScrollToTop();
    }

    handleSubmit(event) {
        event.preventDefault();
        let state = this.state;
        let invalid = [...state.invalid];
        if (state.selectedAreas.length === 0 && !state.otherAreas) Form.arrayAdd(invalid, 'otherAreas');
        if (!state.recaptcha) Form.arrayAdd(invalid, 'recaptcha');
        if (invalid.length) {
            this.setState({
                invalid: invalid,
                retries: 0
            });
        } else {
            let formData = new FormData();
            formData.append('FirstName', state.firstName);
            formData.append('LastName', state.lastName);
            formData.append('Email', state.email);
            formData.append('ConfirmEmail', state.confirmEmail);
            formData.append('ResumeFile', state.upload);
            state.selectedAreas.forEach(a => formData.append('ResearchAreas', a)),
            formData.append('OtherResearchAreas', state.otherAreas);
            formData.append('Website', state.website);
            formData.append('__RequestVerificationToken', token);
            formData.append('g-recaptcha-response', state.recaptcha);

            this.setState({
                submitting: 1
            });

            axios
                .post(postPath, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                })
                .then(() => {
                    this.setState({
                        submitted: true,
                        submitting: 0,
                        page: 1,
                        firstName: '',
                        lastName: '',
                        email: '',
                        confirmEmail: '',
                        upload: null,
                        uploadName: '',
                        website: '',
                        selectedAreas: [],
                        otherAreas: '',
                        retries: 0,
                        invalid: []
                    });
                    this.handleScrollToTop();
                })
                .catch(err => {
                    if (err && err.response && err.response.data) {
                        let data = err.response.data;
                        if (data && typeof data === 'object' && (data['-'] || data['g-recaptcha-response'])) {
                            // Failed to save the submitted file; allow resubmission
                            this.recaptchaRef.current.reset();
                            let invalid = [...this.state.invalid];
                            if (this.state.retries < 2) {
                                Form.arrayAdd(invalid, 'recaptcha');
                            }
                            this.setState({
                                submitting: 0,
                                recaptcha: '',
                                retries: this.state.retries + 1,
                                invalid: invalid
                            });
                            return;
                        }
                    }

                    this.setState({
                        page: 1,
                        submitted: true,
                        submitting: 0,
                        failed: true
                    });
                    this.handleScrollToTop();
                });
        }
    }

    handleClickWhileSubmitting(event) {
        event.preventDefault();
        this.setState({
            submitting: this.state.submitting + 1
        });
    }

    render() {
        let formPage,
            selectedAreas = this.state.selectedAreas,
            yourSelections,
            nav,
            reviewerMessage,
            buttonText,
            submitButtons,
            retryMessage,
            retryReloadButton,
            upload,
            state = this.state,
            isInvalid = fieldName => state.invalid.indexOf(fieldName) >= 0,
            fieldClass = (fieldName, classNames, other) => cn(classNames, { 'invalid': isInvalid(fieldName) }, other);

        if (state.page === 1) {
            buttonText = 'Apply to Become a Reviewer';
            if (state.submitted) {
                if (state.failed) {
                    reviewerMessage = <p>We were unable to complete your submission. Please click the button below to reload the form and try again. If this problem persists,
                        please contact us using the information at the bottom of the page.</p>;
                    formPage = <a href="#" role="button" className="rf-apply" onClick={this.handleRefresh}>Click to Start Over</a>;
                } else {
                    reviewerMessage = <p>You have successfully submitted your CV to the <em>NIDDK Review Branch</em>. Thank you for your interest in becoming a reviewer for the NIDDK. The button below will reset the form to submit additional individuals for consideration as needed.</p>;
                    buttonText = 'Submit Another Application';
                }
            }
            formPage = formPage || <a href="#" role="button" className="rf-apply" onClick={this.handleApply}>{buttonText}</a>;
        }

        if (state.page === 2) {
            nav = <div className="reviewer-nav">
                <span className="rf-current">1</span>
                <i className="i-thick-right"></i>
                <span>2</span>
            </div>;

            reviewerMessage = <p>All fields on this page are required.</p>;

            if (state.upload) {
                upload = <div className="selection" aria-label="Selected file for upload"><i className="i-times delete-selection" tabIndex="0" role="button" title="Deselect this file" aria-label="Deselect this file" onClick={this.handleDeleteUpload} onKeyDown={this.handleDeleteUploadKeyDown}></i>{state.uploadName}</div>;
            }

            formPage = <div className="form-page">
                <form className="dk-form">
                    {/*Users must fill in all form elements on the page*/}
                    <div className={fieldClass('firstName', 'field text required')}>
                        <label htmlFor="reviewer-firstName">First Name</label>
                        <input id="reviewer-firstName" type="text" name="firstName" value={state.firstName} onChange={this.handleChange} />
                        <div className="validation">Please enter your first name.</div>
                    </div>
                    <div className={fieldClass('lastName', 'field text required')}>
                        <label htmlFor="reviewer-lastName">Last Name</label>
                        <input id="reviewer-lastName" type="text" name="lastName" value={state.lastName} onChange={this.handleChange} />
                        <div className="validation">Please enter your last name.</div>
                    </div>
                    <div className={fieldClass('email', 'field text required')}>
                        <label htmlFor="reviewer-email">Email</label>
                        <input id="reviewer-email" type="text" name="email" value={state.email} onChange={this.handleChange} />
                        <div className="validation">Please enter a valid email address.</div>
                    </div>
                    {/*Confirm that the @ symbol is used in the email address*/}
                    <div className={fieldClass('confirmEmail', 'field text required')}>
                        <label htmlFor="reviewer-confirmEmail">Confirm Email</label>
                        <input id="reviewer-confirmEmail" type="text" name="confirmEmail" value={state.confirmEmail} onChange={this.handleChange} />
                        <div className="validation">Please re-enter the same email address.</div>
                    </div>
                    {/*User can manually upload one or multiple files that are .doc, .docx, or .pdf format.
                        Drag-and-drop box option should be available for user to upload as well.
                    */}
                    <div className={fieldClass('upload', 'field upload required rf-up')}>
                        <label htmlFor="reviewer-attachment">Biosketch, CV, or Resume</label>
                        {upload}
                        <p>Upload your document in Microsoft Word (.doc, .docx) or PDF (.pdf) format with a maximum file size of 4 MB.</p>
                        <input ref={this.uploadRef} type="file" id="reviewer-attachment" onChange={this.handleUpload} name="attachment" accept=".doc, .docx, .pdf" />
                        <div className="validation">{state.uploadValidation}</div>
                    </div>
                    <div className="buttons">
                        <button type="button" className="blue" onClick={this.handleNext}>Next</button>
                    </div>
                </form>
            </div>;
        }
        else if (state.page === 3) {
            if (selectedAreas.length > 0) {
                yourSelections = <div className="your-selections">
                    <h4>Your Selections</h4>
                    <a href="#" tabIndex="0" role="button" className="clear-selections" onClick={this.handleClearSelections}>Clear All</a>
                    <div>
                        {selectedAreas.map((area, key) => {
                            const title = 'Deselect ' + area;
                            return <div className="selection" key={key}><i tabIndex="0" role="button" className="i-times delete-selection" aria-label={title} title={title} data-val={area} onClick={this.handleDeleteSelection} onKeyDown={this.handleDeleteSelectionKeyDown}></i>{area}</div>;
                        })}
                    </div>
                </div>;
            }
            else {
                yourSelections = <p className="rf-helper">Please select your research areas, or describe your research below.</p>;
            }

            reviewerMessage = <p>Fields marked with asterisks (<span className="red-required">*</span>) are required.</p>;

            if (state.submitting) {
                nav = <div className="reviewer-nav">
                    <span tabIndex="0" className="rf-previous">1</span>
                    <i className="i-thick-right"></i>
                    <span className="rf-current">2</span>
                </div>;
                retryMessage = state.submitting > 1 ? <div className="validation">Please wait while your information is being submitted.</div> : null;
                submitButtons = <div className="buttons">
                    <button type="button" onClick={this.handleClickWhileSubmitting}>Back</button>
                    <button type="submit" onClick={this.handleClickWhileSubmitting}>Submitting...</button>
                    {retryMessage}
                </div>;
            } else {
                nav = <div className="reviewer-nav">
                    <a href="#" aria-label="Return to previous page" className="rf-previous" onClick={this.handleBack}>1</a>
                    <i className="i-thick-right"></i>
                    <span className="rf-current">2</span>
                </div>;
                if (state.retries) {
                    if (state.retries > 1) {
                        retryReloadButton = <button type="button" onClick={this.handleRefresh}>Start Over</button>;
                    }
                    if (state.retries < 2) {
                        retryMessage = <div className="validation">An error occurred during submission. Please try submitting again{(state.retries > 1 ? ', or click Start Over to restart' : '')}.</div>;
                    } else {
                        retryMessage = <div className="validation">We were unable to process your submission. Please click Start Over to attempt your submission again.</div>;
                    }
                }
                submitButtons = <div className="buttons">
                    {state.retries < 2 ? <button type="button" onClick={this.handleBack}>Back</button> : null}
                    {state.retries < 2 ? <button type="submit" className="blue" onClick={this.handleSubmit}>Submit{(state.retries ? ' Again' : '')}</button> : null}
                    {retryReloadButton}
                    {retryMessage}
                </div>;
            }

            formPage = <div className="form-page">
                <form className="dk-form">
                    <ResearchAreas source={getResAreasPath} />
                    {yourSelections}
                    <div className={fieldClass('otherAreas', 'field text', { 'required': !selectedAreas.length })}>
                        <label htmlFor="reviewer-otherAreas">Other Research</label>
                        <input type="text" id="reviewer-otherAreas" name="otherAreas" value={state.otherAreas} onChange={this.handleChange} />
                        <div className="validation">
                            Please select at least one research area above or describe your research here.
                        </div>
                    </div>
                    <p className="rf-helper gap">Do you have a website detailing your work? Please share it with us.</p>
                    <div className="field text">
                        <label htmlFor="reviewer-website">Website</label>
                        <input type="text" id="reviewer-website" name="website" value={state.website} onChange={this.handleChange} />
                    </div>
                    <div className={fieldClass('recaptcha', 'field recaptcha')}>
                        <ReCAPTCHA ref={this.recaptchaRef} sitekey={recaptchaKey} onChange={this.handleRecaptchaChange} />
                        <div className="validation">
                            Please complete the reCAPTCHA challenge.
                        </div>
                    </div>
                    {submitButtons}
                </form>
            </div>;
        }

        return (
            <div id="reviewer-form">
                {nav}
                {reviewerMessage}
                <ResearchAreaSelection.Provider value={{
                    state: state,
                    handleAreaCheck: e => {
                        const target = e.target;
                        let invalid = [...state.invalid];
                        Form.arrayRemove(invalid, 'otherAreas');
                        if (target.checked) {
                            this.setState({
                                selectedAreas: state.selectedAreas.concat(target.value),
                                invalid: invalid
                            });
                        }
                        else {
                            this.setState({
                                selectedAreas: state.selectedAreas.filter(area => area !== target.value)
                            });
                        }
                    }
                }}>
                    {formPage}
                </ResearchAreaSelection.Provider>
            </div>
        );
    }
}

root.render(e(Form));
